import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'YOUR_WEB3FORMS_ACCESS_KEY', // Replace with your Web3Forms key
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          subject: `New Contact Inquiry from ${formData.name} - LMR IMPEX`,
          from_name: 'LMR IMPEX Website',
          to_email: 'admin@lmrimpex.com'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        alert('Thank you! Your message has been sent successfully.');
      } else {
        setStatus('error');
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['LMR IMPEX', 'Visakhapatnam', 'Andhra Pradesh, India']
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91-9866019988', 'Mon - Sat, 9 AM - 6 PM']
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['admin@lmrimpex.com', 'Quick response guaranteed']
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Saturday', '9:00 AM - 6:00 PM']
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="contact-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Get in Touch</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Let's Build<br />
              Something Great<br />
              Together.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              Whether you need furniture, interiors, or export services, our team is ready to help. 
              Reach out today and let's discuss your project.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white" data-testid="contact-info">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 border border-slate-200 p-6"
                data-testid={`contact-info-${index}`}
              >
                <info.icon className="w-8 h-8 text-bronze-500 mb-4" />
                <h3 className="text-lg font-medium mb-3">{info.title}</h3>
                {info.details.map((detail, i) => (
                  <p key={i} className="text-muted-foreground text-sm">{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-24" data-testid="contact-form-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Send Us a Message</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                We'd Love to Hear From You
              </h2>
              <p className="text-muted-foreground mb-8">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="contact-name-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contact-email-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contact-phone-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <input
                  type="text"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  data-testid="contact-company-input"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                />
                <textarea
                  placeholder="Your Message *"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="contact-message-input"
                  rows="6"
                  className="w-full px-4 py-3 border border-slate-200 focus:outline-none focus:border-slate-900 transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="w-full bg-slate-900 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative h-96 lg:h-full"
              data-testid="contact-map"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243647.2599385104!2d83.09837707655952!3d17.738310663594056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a39431389e6973f%3A0x92d9c20395498468!2sVisakhapatnam%2C%20Andhra%20Pradesh!5e0!3m2!1sen!2sin!4v1234567890123"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
                title="LMR IMPEX Location"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 text-white" data-testid="contact-cta">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Prefer to Talk Directly?
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-white/80 mb-8 max-w-2xl mx-auto">
              Our team is available Monday through Saturday from 9 AM to 6 PM.
            </p>
            <a 
              href="tel:+91XXXXXXXXXX" 
              data-testid="contact-call-button"
              className="inline-flex items-center bg-bronze-500 text-white hover:bg-bronze-600 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
            >
              Call Now: +91 XXXXX XXXXX
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
